import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, ChevronUp, ChevronDown, Download, Columns3, Bookmark, Plus, Trash2, Pin, PinOff, Archive, ArchiveRestore, MoreVertical, Pencil } from "lucide-react";
import { toast } from "sonner";
import { LOAN_STAGES } from "@/components/StagePill";

const daysAgo = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
  if (d < 1) return "today";
  if (d < 2) return "1d";
  return `${Math.floor(d)}d`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "2-digit", month: "short", day: "numeric" });
};

const STAGE_COLORS = {
  Draft: "text-slate-500",
  Submitted: "text-blue-700",
  "Initial Review": "text-blue-700",
  Assigned: "text-blue-700",
  Underwriting: "text-amber-700",
  "Conditional Approval": "text-amber-700",
  "Need Additional Documents": "text-amber-700",
  Approved: "text-emerald-700",
  Funded: "text-emerald-700",
  Declined: "text-red-700",
};

// Columns; visibility controlled per role
const ALL_COLUMNS = [
  { key: "loan_number", label: "File #", w: "w-[130px]", format: (a) => a.loan_number },
  { key: "contact_name", label: "Contact", w: "w-[180px]", format: (a) => a.contact_name || a.borrower_name || "—" },
  { key: "borrower_email", label: "Email", w: "w-[220px]", format: (a) => a.borrower_email || "—", roles: ["admin", "loan_rep", "broker"] },
  { key: "borrower_phone", label: "Phone", w: "w-[150px]", format: (a) => a.borrower_phone || "—", roles: ["admin", "loan_rep", "broker"] },
  { key: "business_name", label: "Business", w: "w-[210px]", format: (a) => a.business_name || "—" },
  { key: "product_name", label: "Program", w: "w-[190px]", format: (a) => a.product_name },
  { key: "purpose", label: "Purpose", w: "w-[230px]", format: (a) => a.purpose ? (a.purpose.length > 40 ? a.purpose.slice(0, 40) + "…" : a.purpose) : "—" },
  {
    key: "stage",
    label: "Position",
    w: "w-[190px]",
    format: (a) => (
      <span className={`small-caps ${STAGE_COLORS[a.stage] || "text-slate-700"}`}>{a.stage}</span>
    ),
  },
  { key: "loan_rep_name", label: "Officer", w: "w-[160px]", format: (a) => a.loan_rep?.name || "—", sortValue: (a) => a.loan_rep?.name || "" },
  { key: "broker_name", label: "Advisor", w: "w-[160px]", format: (a) => a.broker?.name || "—", roles: ["admin"], sortValue: (a) => a.broker?.name || "" },
  { key: "industry", label: "Sector", w: "w-[140px]", format: (a) => a.industry || "—" },
  {
    key: "annual_revenue",
    label: "Revenue",
    w: "w-[130px]",
    right: true,
    format: (a) => (a.annual_revenue ? fmtMoney(a.annual_revenue) : "—"),
    sortValue: (a) => Number(a.annual_revenue || 0),
  },
  {
    key: "time_in_business",
    label: "Yrs in biz",
    w: "w-[110px]",
    right: true,
    format: (a) => (a.time_in_business ? `${a.time_in_business}` : "—"),
    sortValue: (a) => Number(a.time_in_business || 0),
  },
  {
    key: "fico",
    label: "FICO",
    w: "w-[100px]",
    right: true,
    format: (a) => (a.fico ? String(a.fico) : "—"),
    sortValue: (a) => Number(a.fico || 0),
  },
  {
    key: "created_at",
    label: "Submitted",
    w: "w-[110px]",
    format: (a) => fmtDate(a.created_at),
    sortValue: (a) => (a.created_at ? new Date(a.created_at).getTime() : 0),
  },
  {
    key: "days_in_stage",
    label: "Age",
    w: "w-[80px]",
    format: (a) => daysAgo(a.updated_at || a.created_at),
    sortValue: (a) => (a.updated_at || a.created_at ? new Date(a.updated_at || a.created_at).getTime() : 0),
  },
];

// Default columns per role (rest are hideable via picker)
const DEFAULT_VISIBLE = {
  admin: ["loan_number", "contact_name", "borrower_email", "borrower_phone", "business_name", "product_name", "requested_amount", "stage", "loan_rep_name", "broker_name", "industry", "annual_revenue", "created_at", "days_in_stage"],
  loan_rep: ["loan_number", "contact_name", "borrower_email", "borrower_phone", "business_name", "product_name", "requested_amount", "stage", "industry", "annual_revenue", "fico", "created_at", "days_in_stage"],
  broker: ["loan_number", "contact_name", "borrower_email", "business_name", "product_name", "requested_amount", "stage", "loan_rep_name", "industry", "created_at", "days_in_stage"],
  borrower: ["loan_number", "product_name", "requested_amount", "stage", "created_at", "days_in_stage"],
};

function toCSV(rows, columns) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => c.label).join(",");
  const numericKeys = new Set(["requested_amount", "annual_revenue", "time_in_business", "fico"]);
  const body = rows
    .map((a) =>
      columns
        .map((c) => {
          let val;
          if (c.key === "loan_rep_name") val = a.loan_rep?.name || "";
          else if (c.key === "broker_name") val = a.broker?.name || "";
          else if (c.key === "days_in_stage") val = a.updated_at || a.created_at || "";
          else if (numericKeys.has(c.key)) val = Number(a[c.key] || 0);
          else val = a[c.key] ?? "";
          return esc(val);
        })
        .join(",")
    )
    .join("\n");
  return `${header}\n${body}`;
}

export default function DealsTable({ title = "Deals", subtitle = "Spreadsheet view" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // Read URL params up-front so first render already has the right filter state.
  const initialStage = params.get("stage") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [stageFilter, setStageFilter] = useState(initialStage);
  const [uwFilter, setUwFilter] = useState(false);
  const [repFilter, setRepFilter] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [views, setViews] = useState([]);
  const [showViews, setShowViews] = useState(false);
  const [savingName, setSavingName] = useState("");
  const [activeViewId, setActiveViewId] = useState(null);
  const [showArchived, setShowArchived] = useState(Boolean(initialStage));
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // application
  const [allLoanReps, setAllLoanReps] = useState([]); // admin roster for filter
  const canManage = user?.role === "loan_rep" || user?.role === "admin";
  const canDelete = user?.role === "admin";

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/users")
      .then((r) => {
        const reps = (r.data || [])
          .filter((u) => u.role === "loan_rep" && u.status !== "archived" && !u.archived)
          .map((u) => ({ id: u.id, name: u.name || u.email || "—" }));
        setAllLoanReps(reps);
      })
      .catch(() => setAllLoanReps([]));
  }, [user?.role]);

  const loadViews = () =>
    api
      .get("/deal-views")
      .then((r) => setViews(r.data || []))
      .catch(() => setViews([]));
  useEffect(() => { loadViews(); }, []);

  // per-user column visibility persisted in localStorage
  const storageKey = `vn_deals_cols_${user?.role || "guest"}`;
  const [visibleKeys, setVisibleKeys] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE[user?.role] || DEFAULT_VISIBLE.borrower;
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(visibleKeys)); } catch { /* ignore */ }
  }, [visibleKeys, storageKey]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/applications", { params: showArchived ? { include_archived: true } : {} })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [showArchived]);

  const reload = () => {
    api
      .get("/applications", { params: showArchived ? { include_archived: true } : {} })
      .then((r) => setRows(r.data || []))
      .catch(() => {});
  };

  const archiveApp = async (app) => {
    try {
      await api.post(`/applications/${app.id}/archive`);
      toast.success("File archived");
      setOpenMenuId(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unarchiveApp = async (app) => {
    try {
      await api.post(`/applications/${app.id}/unarchive`);
      toast.success("File restored");
      setOpenMenuId(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePinApp = async (app) => {
    try {
      const { data } = await api.post(`/applications/${app.id}/pin`);
      toast.success(data.pinned ? "Pinned to top" : "Unpinned");
      setOpenMenuId(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteApp = async (app) => {
    try {
      await api.delete(`/applications/${app.id}`);
      toast.success("File permanently deleted");
      setConfirmDelete(null);
      setOpenMenuId(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const availableColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => !c.roles || c.roles.includes(user?.role)),
    [user?.role]
  );
  const columns = useMemo(
    () => availableColumns.filter((c) => visibleKeys.includes(c.key)),
    [availableColumns, visibleKeys]
  );

  const toggleCol = (key) => {
    setVisibleKeys((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]
    );
  };

  // Saved views: apply / save / delete
  const applyView = (v) => {
    const c = v.config || {};
    if (c.stage != null) setStageFilter(c.stage || "");
    if (c.sort_key) setSortKey(c.sort_key);
    if (c.sort_dir) setSortDir(c.sort_dir);
    if (Array.isArray(c.visible_keys) && c.visible_keys.length) setVisibleKeys(c.visible_keys);
    if (typeof c.q === "string") setQ(c.q);
    setActiveViewId(v.id);
    setShowViews(false);
    toast.success(`View "${v.name}" applied`);
  };
  const saveCurrentAsView = async () => {
    const name = savingName.trim();
    if (!name) return;
    try {
      const config = {
        stage: stageFilter,
        sort_key: sortKey,
        sort_dir: sortDir,
        visible_keys: visibleKeys,
        q,
      };
      const r = await api.post("/deal-views", { name, config });
      setSavingName("");
      setActiveViewId(r.data.id);
      loadViews();
      toast.success(`View "${name}" saved`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteView = async (v) => {
    if (!confirm(`Delete view "${v.name}"?`)) return;
    try {
      await api.delete(`/deal-views/${v.id}`);
      if (activeViewId === v.id) setActiveViewId(null);
      loadViews();
      toast.success("View removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePin = async (v) => {
    try {
      await api.patch(`/deal-views/${v.id}/pin`, { pinned: !v.pinned });
      loadViews();
      // notify the layout so the sidebar refreshes
      window.dispatchEvent(new Event("vn:views-changed"));
      toast.success(v.pinned ? "Unpinned from sidebar" : "Pinned to sidebar");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Auto-apply view from ?view= query param (used by sidebar pinned links)
  useEffect(() => {
    const wanted = params.get("view");
    if (!wanted || !views.length) return;
    const found = views.find((v) => v.id === wanted);
    if (found && activeViewId !== found.id) {
      applyView(found);
      // strip the query param so refresh doesn't re-trigger
      const next = new URLSearchParams(params);
      next.delete("view");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, views]);

  // Auto-apply stage from ?stage= query param (Overview stage tiles link here).
  // Also enable "show archived" so terminal states like Declined/Funded appear.
  // Note: initial state already reads params up-front; this handles subsequent
  // navigation changes without a remount.
  useEffect(() => {
    const wantedStage = params.get("stage") || "";
    if (wantedStage !== stageFilter) setStageFilter(wantedStage);
    if (wantedStage && !showArchived) setShowArchived(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const UW_STAGES = ["Assigned", "Underwriting", "Conditional Approval", "Need Additional Documents"];

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let out = rows;
    if (uwFilter) out = out.filter((r) => UW_STAGES.includes(r.stage));
    if (stageFilter) out = out.filter((r) => r.stage === stageFilter);
    if (repFilter) {
      out = out.filter((r) =>
        repFilter === "__unassigned__"
          ? !r.loan_rep?.id
          : r.loan_rep?.id === repFilter
      );
    }
    if (query) {
      out = out.filter((r) =>
        [
          r.loan_number, r.borrower_name, r.business_name, r.product_name,
          r.industry, r.loan_rep?.name, r.broker?.name, r.stage,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query)
      );
    }
    const col = columns.find((c) => c.key === sortKey) || columns[0];
    const sv = col?.sortValue || ((r) => (r[col.key] || "").toString().toLowerCase());
    const dir = sortDir === "asc" ? 1 : -1;
    return [...out].sort((a, b) => {
      // Pinned rows always float to the top, most-recently-pinned first,
      // regardless of the active sort column.
      const ap = a.pinned ? 1 : 0;
      const bp = b.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;
      if (ap && bp) {
        const at = a.pinned_at || "";
        const bt = b.pinned_at || "";
        if (at !== bt) return at > bt ? -1 : 1;
      }
      const va = sv(a);
      const vb = sv(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rows, q, sortKey, sortDir, columns, stageFilter, uwFilter, repFilter]);

  const stages = useMemo(() => {
    // Union canonical stages (so options work before rows load) with any stages
    // actually present on visible rows.
    const seen = new Set(LOAN_STAGES);
    rows.forEach((r) => { if (r.stage) seen.add(r.stage); });
    // Preserve canonical order, then append any rogue stages at the end.
    const canonical = LOAN_STAGES.filter((s) => seen.has(s));
    const extras = Array.from(seen).filter((s) => !LOAN_STAGES.includes(s));
    return [...canonical, ...extras];
  }, [rows]);

  const loanReps = useMemo(() => {
    const map = new Map();
    // Seed with the full admin roster so reps with 0 deals are still filterable.
    allLoanReps.forEach((u) => map.set(u.id, u));
    // Add anyone showing up on a row who wasn't in the roster (fallback).
    rows.forEach((r) => {
      if (r.loan_rep?.id && !map.has(r.loan_rep.id)) {
        map.set(r.loan_rep.id, { id: r.loan_rep.id, name: r.loan_rep.name || r.loan_rep.email || "—" });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, allLoanReps]);

  const totals = useMemo(() => {
    const vol = filtered.reduce((s, r) => s + Number(r.requested_amount || 0), 0);
    return { count: filtered.length, volume: vol };
  }, [filtered]);

  const clickSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const exportCSV = () => {
    const csv = toCSV(filtered, columns);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `venture-deals-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section data-testid="deals-table">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="small-caps text-slate-500">{title}</div>
          <h2 className="mt-3 font-semibold text-[32px] text-[#1F2937]">{subtitle}</h2>
        </div>
        <div className="small-caps text-slate-500 text-[11px] font-mono">
          {totals.count} {totals.count === 1 ? "deal" : "deals"} · {fmtMoney(totals.volume)}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3 border-b border-slate-300 pb-1 flex-1 min-w-[280px] max-w-md">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search file #, contact, business, program…"
            data-testid="deals-table-search"
            className="w-full bg-transparent border-0 outline-none py-1 text-[14px] placeholder:text-slate-400 text-[#1F2937]"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          data-testid="deals-table-stage-filter"
          className="bg-transparent border-b border-slate-300 py-1 text-[14px] font-semibold text-[#1F2937] focus:outline-none focus:border-[#1F2937]"
        >
          <option value="">All positions</option>
          {stages.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {user?.role === "admin" && (
          <select
            value={repFilter}
            onChange={(e) => setRepFilter(e.target.value)}
            data-testid="deals-table-rep-filter"
            className="bg-transparent border-b border-slate-300 py-1 text-[14px] font-semibold text-[#1F2937] focus:outline-none focus:border-[#1F2937]"
          >
            <option value="">All loan reps</option>
            <option value="__unassigned__">Unassigned</option>
            {loanReps.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        )}
        <button
          onClick={() => setUwFilter((v) => !v)}
          data-testid="deals-table-uw-filter"
          className={`inline-flex items-center gap-2 small-caps px-3 py-1 border transition-colors ${
            uwFilter
              ? "bg-[#1F2937] text-white border-[#1F2937]"
              : "border-slate-300 text-[#1F2937] hover:border-[#1F2937]"
          }`}
          title="Show deals moving to (or in) Underwriting"
        >
          → Underwriting
        </button>
        <button
          onClick={exportCSV}
          className="inline-flex items-center gap-2 small-caps text-[#1F2937] hover:opacity-70"
          data-testid="deals-table-export"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
        {canManage && (
          <label
            className="inline-flex items-center gap-2 small-caps text-[#1F2937] cursor-pointer select-none"
            data-testid="deals-show-archived-label"
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="deals-show-archived"
              className="h-3.5 w-3.5"
            />
            Show archived
          </label>
        )}
        <div className="relative">
          <button
            onClick={() => { setShowViews((s) => !s); setShowPicker(false); }}
            className="inline-flex items-center gap-2 small-caps text-[#1F2937] hover:opacity-70"
            data-testid="deals-table-views-btn"
          >
            <Bookmark className="h-3.5 w-3.5" />
            {activeViewId
              ? views.find((v) => v.id === activeViewId)?.name || "Views"
              : `Views (${views.length})`}
          </button>
          {showViews && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowViews(false)} />
              <div
                className="absolute right-0 top-full mt-2 z-40 w-72 bg-white border border-slate-300 shadow-lg py-2"
                data-testid="deals-table-views-menu"
              >
                <div className="px-4 py-2 small-caps text-slate-500 text-[10px] border-b border-slate-200">
                  Saved views
                </div>
                {views.length === 0 && (
                  <div className="px-4 py-3 text-[12px] text-slate-500">No saved views yet.</div>
                )}
                {views.map((v) => (
                  <div
                    key={v.id}
                    className={`px-4 py-2 flex items-center justify-between gap-2 text-[13px] hover:bg-slate-50 group ${
                      activeViewId === v.id ? "bg-slate-50" : ""
                    }`}
                    data-testid={`deals-view-row-${v.id}`}
                  >
                    <button
                      onClick={() => applyView(v)}
                      className="text-left flex-1 truncate text-[#1F2937] inline-flex items-center gap-2"
                      data-testid={`deals-view-apply-${v.id}`}
                    >
                      {v.pinned && <Pin className="h-3 w-3 text-[#1F2937]" />}
                      <span className="truncate">{v.name}</span>
                    </button>
                    <button
                      onClick={() => togglePin(v)}
                      className={`text-slate-400 hover:text-[#1F2937] ${v.pinned ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                      data-testid={`deals-view-pin-${v.id}`}
                      title={v.pinned ? "Unpin from sidebar" : "Pin to sidebar"}
                    >
                      {v.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                    </button>
                    <button
                      onClick={() => deleteView(v)}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#1F2937]"
                      data-testid={`deals-view-delete-${v.id}`}
                      title="Delete view"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <div className="border-t border-slate-200 mt-2 pt-3 px-4 pb-2">
                  <div className="small-caps text-slate-500 text-[10px] mb-2">Save current as…</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={savingName}
                      onChange={(e) => setSavingName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveCurrentAsView()}
                      placeholder="e.g., Underwriting queue"
                      data-testid="deals-view-save-input"
                      className="flex-1 bg-transparent border-b border-slate-300 py-1 text-[13px] focus:outline-none focus:border-[#1F2937]"
                    />
                    <button
                      onClick={saveCurrentAsView}
                      disabled={!savingName.trim()}
                      className="inline-flex items-center gap-1 small-caps text-[#1F2937] disabled:opacity-40"
                      data-testid="deals-view-save-btn"
                    >
                      <Plus className="h-3 w-3" /> Save
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => { setShowPicker((s) => !s); setShowViews(false); }}
            className="inline-flex items-center gap-2 small-caps text-[#1F2937] hover:opacity-70"
            data-testid="deals-table-columns-btn"
          >
            <Columns3 className="h-3.5 w-3.5" /> Columns ({columns.length})
          </button>
          {showPicker && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setShowPicker(false)}
              />
              <div
                className="absolute right-0 top-full mt-2 z-40 w-64 bg-white border border-slate-300 shadow-lg py-2 max-h-[380px] overflow-auto"
                data-testid="deals-table-columns-menu"
              >
                <div className="px-4 py-2 small-caps text-slate-500 text-[10px] border-b border-slate-200">
                  Show columns
                </div>
                {availableColumns.map((c) => (
                  <label
                    key={c.key}
                    className="flex items-center gap-3 px-4 py-2 text-[13px] text-[#1F2937] hover:bg-slate-50 cursor-pointer select-none"
                    data-testid={`deals-col-toggle-${c.key}`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={visibleKeys.includes(c.key)}
                      onChange={() => toggleCol(c.key)}
                    />
                    {c.label}
                  </label>
                ))}
                <div className="border-t border-slate-200 mt-2 pt-2 px-4 pb-2 flex items-center justify-between">
                  <button
                    onClick={() => setVisibleKeys(availableColumns.map((c) => c.key))}
                    className="small-caps text-[10px] text-[#1F2937] hover:opacity-70"
                    data-testid="deals-col-all"
                  >
                    Show all
                  </button>
                  <button
                    onClick={() => setVisibleKeys(DEFAULT_VISIBLE[user?.role] || DEFAULT_VISIBLE.borrower)}
                    className="small-caps text-[10px] text-slate-500 hover:text-[#1F2937]"
                    data-testid="deals-col-reset"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 border border-slate-200 overflow-x-auto" data-testid="deals-table-scroll">
        <table className="min-w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => clickSort(c.key)}
                  className={`${c.w} px-4 py-3 small-caps text-slate-600 text-left cursor-pointer select-none whitespace-nowrap ${c.right ? "text-right" : ""}`}
                  data-testid={`deals-th-${c.key}`}
                >
                  <span className={`inline-flex items-center gap-1 ${c.right ? "justify-end w-full" : ""}`}>
                    {c.label}
                    {sortKey === c.key && (
                      sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </span>
                </th>
              ))}
              {canManage && (
                <th className="w-[60px] px-2 py-3 small-caps text-slate-600 text-right" data-testid="deals-th-actions">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + (canManage ? 1 : 0)} className="px-4 py-16 text-center small-caps text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (canManage ? 1 : 0)} className="px-4 py-16 text-center">
                  <div className="font-semibold text-[16px] text-slate-500">No deals match.</div>
                </td>
              </tr>
            ) : (
              filtered.map((a, i) => (
                <tr
                  key={a.id}
                  onClick={(e) => {
                    // Don't navigate if clicking on the actions cell
                    if (e.target.closest("[data-row-actions]")) return;
                    nav(`/app/applications/${a.id}`);
                  }}
                  data-testid={`deals-row-${a.id}`}
                  className={`cursor-pointer border-b border-slate-100 hover:bg-slate-800/[0.04] ${
                    a.archived ? "bg-slate-100/70 text-slate-400" : (i % 2 === 1 ? "bg-slate-50/40" : "bg-white")
                  }`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-3 whitespace-nowrap ${c.right ? "text-right font-mono" : ""} ${
                        a.archived ? "text-slate-400" :
                        c.key === "loan_number" ? "font-mono text-slate-500" :
                        c.key === "business_name" || c.key === "product_name" ? "font-semibold text-[#1F2937]" :
                        "text-slate-700"
                      }`}
                    >
                      {c.key === "loan_number" ? (
                        <span className="inline-flex items-center gap-2">
                          {a.pinned && (
                            <Pin
                              className="h-3 w-3 text-[#1F2937] fill-[#1F2937]"
                              data-testid={`deals-row-pinned-indicator-${a.id}`}
                            />
                          )}
                          {a.archived && (
                            <span className="text-[9px] uppercase tracking-wider border border-slate-400 text-slate-500 px-1">Arch</span>
                          )}
                          {c.format(a)}
                        </span>
                      ) : (
                        c.format(a)
                      )}
                    </td>
                  ))}
                  {canManage && (
                    <td className="px-2 py-3 text-right relative" data-row-actions>
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === a.id ? null : a.id); }}
                        className="text-slate-400 hover:text-[#1F2937] p-1"
                        data-testid={`deals-row-menu-${a.id}`}
                        title="Actions"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {openMenuId === a.id && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); }} />
                          <div
                            className="absolute right-2 top-full mt-1 z-40 w-44 bg-white border border-slate-300 shadow-lg py-1 text-left"
                            data-testid={`deals-row-menu-panel-${a.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => togglePinApp(a)}
                              className="w-full text-left px-3 py-2 text-[13px] text-[#1F2937] hover:bg-slate-50 inline-flex items-center gap-2"
                              data-testid={`deals-row-pin-${a.id}`}
                            >
                              {a.pinned ? (
                                <><PinOff className="h-3.5 w-3.5" /> Unpin</>
                              ) : (
                                <><Pin className="h-3.5 w-3.5" /> Pin to top</>
                              )}
                            </button>
                            <button
                              onClick={() => { setOpenMenuId(null); nav(`/app/applications/${a.id}?edit=1`); }}
                              className="w-full text-left px-3 py-2 text-[13px] text-[#1F2937] hover:bg-slate-50 inline-flex items-center gap-2"
                              data-testid={`deals-row-edit-${a.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </button>
                            {a.archived ? (
                              <button
                                onClick={() => unarchiveApp(a)}
                                className="w-full text-left px-3 py-2 text-[13px] text-[#1F2937] hover:bg-slate-50 inline-flex items-center gap-2"
                                data-testid={`deals-row-unarchive-${a.id}`}
                              >
                                <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                              </button>
                            ) : (
                              <button
                                onClick={() => archiveApp(a)}
                                className="w-full text-left px-3 py-2 text-[13px] text-[#1F2937] hover:bg-slate-50 inline-flex items-center gap-2"
                                data-testid={`deals-row-archive-${a.id}`}
                              >
                                <Archive className="h-3.5 w-3.5" /> Archive
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => { setOpenMenuId(null); setConfirmDelete(a); }}
                                className="w-full text-left px-3 py-2 text-[13px] text-red-700 hover:bg-red-50 inline-flex items-center gap-2 border-t border-slate-200 mt-1"
                                data-testid={`deals-row-delete-${a.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" /> Delete permanently
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="delete-confirm-modal"
        >
          <div
            className="w-full max-w-md bg-white border border-slate-300 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="small-caps text-red-700">Permanent deletion</div>
            <h3 className="mt-2 font-semibold text-[22px] text-[#1F2937]">
              Delete {confirmDelete.loan_number}?
            </h3>
            <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
              This will permanently remove this file, all uploaded documents, messages, tasks and notifications.
              This cannot be undone. Consider archiving instead.
            </p>
            <div className="mt-8 flex items-center justify-end gap-6">
              <button
                onClick={() => setConfirmDelete(null)}
                className="small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteApp(confirmDelete)}
                className="small-caps text-red-700 inline-flex items-center gap-2"
                data-testid="delete-confirm-btn"
              >
                <Trash2 className="h-4 w-4" /> Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
